/* eslint-disable angular/document-service */

const defaultOptions = {
  /* padding for each direction */
  padding: {
    top: 10,
    bottom: 60,
    left: 70,
    right: 40,
  },
  width: 300,
  height: 360,
  /* TODO: what labels? */
  showLabels: true,
  colors: {
    primary: '#1DD5A3',
    secondary: '#775FD3',
  },
  upperSpaceMarginPercentage: 1.2, // leave about 20% to top part of the graph
  isBarChart: false, // TODO: get rid of this, just for barchart padding
  timeFormats: {
    day: d3.time.format('%m/%d'),
  },
  tooltipFn: () => '',
};

/*
TODO:
  - generate the svg
  - generate the tooltip element
  - make responsive
*/
export default class Zcd3 {

  /**
   * @param {DOMelement} element - div where svg is placed into, should have svg inside it
   * @param {Array} data - data used by the chart
   * @param {Object} opts - Options for data
   */
  constructor(element, data, opts = {}) {
    // TODO: This is appended to DOM so destroy event is required.
    // Figure out better way to do this...
    const tooltipElement = document.createElement('div');
    tooltipElement.setAttribute('class', 'zcd3-tooltip');
    document.body.appendChild(tooltipElement);

    this.element = element;
    this.tooltipElement = tooltipElement;
    this.tooltipDiv = d3.select(tooltipElement).style('opacity', 0);

    this._options = _.merge({}, defaultOptions, opts);
    this.data = data;
    // This is used to find date from mouse coordinates
    this.bisectDate = d3.bisector(d => d.date).left;
    this.tooltipFn = opts.tooltipFn;
    this.axes = {};
    this.scales = {};
    this.series = [];

    this.createBaseGraph(this.data);
  }

  destroy() {
    this.tooltipElement.remove();
  }

  opt(opt) {
    const value = _.get(this._options, opt);
    if(_.isUndefined(value)) {
      throw new Error(`Zcd3 option ${opt} not defined`);
    }

    return value;
  }

  addSeries(params) {
    const defaultParams = {
      name: `Serie ${this.series.length + 1}`,
      yAxis: 'left',
      color: defaultOptions.colors.primary
    };
    const combinedParams = _.merge(defaultParams, params);

    this.createNewScaleAndAxis(combinedParams);

    this.series.push(params);

    switch (params.type) {
      case 'line':
        this.addLineSeries(combinedParams);
        break;
      case 'bar':
        this.addBarSeries(combinedParams);
        break;
      case 'area':
        this.addAreaSeries(combinedParams);
        break;
    }

    if(params.trendLine) {
      this.createTrendLine(combinedParams);
    }

    this.checkHideLabels();
  }

  createNewScaleAndAxis(params) {
    const self = this;

    if(this.axes[params.yAxis]) return;

    const yScale = d3.scale.linear()
      .domain([0, d3.max(_.map(this.data, params.y)) * this.opt('upperSpaceMarginPercentage')])
      .range([this.opt('height') - this.opt('padding.bottom'), this.opt('padding.top')]);

    // Y axis
    const yAxis = d3.svg.axis()
      .scale(yScale)
      .orient(params.yAxis)
      .outerTickSize(0)
      .ticks(5)
      .tickFormat(params.yAxisTickFormat);

    if(params.yAxis === 'left') {
      yAxis.innerTickSize(-self.opt('width') + self.opt('padding.right') + self.opt('padding.left'));
    }

    const axisGrp = self.svg.append('g')
      .attr('class', 'axis yaxis');

    if(params.yAxis === 'left') {
      axisGrp.attr('transform', 'translate(' + self.opt('padding.left') + ',0)');
    } else {
      axisGrp.attr('transform', 'translate(' + (self.opt('width') - self.opt('padding.right')) + ',0)');
    }
    axisGrp.call(yAxis);

    // Hide the 0 tick
    d3.select(axisGrp.selectAll('.tick')[0][0]).attr('visibility', 'hidden');

    self.scales[params.yAxis] = yScale;
    self.axes[params.yAxis] = yAxis;
  }

  addLineSeries(params) {
    // TODO: remove in D3 4.0 - https://github.com/d3/d3-selection/issues/43
    const self = this;
    const yScale = this.scales[params.yAxis];
    const xScale = this.xScale;

    const line = d3.svg.line()
      .interpolate('monotone')
      .x(d => xScale(d.date))
      .y(d => yScale(d[params.y]));

    self.svg.append('path')
      .datum(self.data)
      .classed('line', true)
      .classed(`serie-${params.name}`, true)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('opacity', 0.75)
      .style('stroke', params.color)
      .style('stroke-width', 2);

    self.svg.selectAll(`circle.point-${params.y}`)
      .data(this.data)
      .enter()
      .append('circle')
      .classed(`point-${params.y}`, true)
      .classed(`serie-${params.name}`, true)
      .attr('r', 3)
      .attr('opacity', 0.3)
      .attr('cx', d => xScale(d.date))
      .attr('cy', d => yScale(d[params.y]))
      .style('fill', params.color);

  }

  checkHideLabels() {
    if(!this.opt('showLabels')) {
      this.svg.selectAll('.axis .tick text')
        .style('opacity', 0);
    }
  }

  addBarSeries(params) {
    // TODO: remove in D3 4.0 - https://github.com/d3/d3-selection/issues/43
    const self = this;
    const yScale = self.scales[params.yAxis];

    const BARWIDTH = (self.opt('width') - self.opt('padding.left') - self.opt('padding.right')) / this.data.length;

    const barGroup = self.svg.selectAll('g.bar-g')
      .data(self.data)
      .enter()
      .append('g')
      .classed('bar-g', true)
      .attr('transform', (d, i) => `translate(${self.xScale(d.date)}, 0)`);

    barGroup.append('rect')
      .attr('y', d => yScale(d[params.y]))
      .attr('height', d => self.opt('height') - self.opt('padding.bottom') - yScale(d[params.y]))
      .attr('width', BARWIDTH / 2) // HOX: why 5?
      .attr('transform', `translate(${-BARWIDTH / 2 / 2 + 1}, 0)`)
      .attr('fill', params.color);
  }

  addAreaSeries(params) {
    // TODO: remove in D3 4.0 - https://github.com/d3/d3-selection/issues/43
    const self = this;
    const xScale = self.xScale;
    const yScale = self.scales[params.yAxis];
    //const tooltipFn = params.tooltipFn;

    const area = d3.svg.area()
      .interpolate('basis')
      .x(d => xScale(moment(d.date).startOf('day')))
      .y0(this.opt('height') - this.opt('padding.bottom'))
      .y1(d => yScale(d[params.y]));

    self.svg.append('path')
      .datum(self.data)
      .classed('area', true)
      .attr('d', area)
      .attr('stroke', params.color)
      .attr('fill', d3.rgb(params.color).brighter(2));

    self.svg.selectAll('.area')
      .attr('opacity', 0.8);
  }

  // TODO: unused now, but should be used in highlighter
  mouseOverHighlight(selector) {
    const self = this;

    return function(d) {
      self.svg.selectAll(`.serie-${selector}`).each(() => {
        const tag = d3.select(this);
        const tagName = tag.node().tagName;

        switch (tagName) {
          case 'path':
          case 'line':
            tag.transition()
              .duration(200)
              .style('stroke-width', '3px');
            break;
          case 'circle':
            tag.transition()
              .duration(200)
              .style('r', 5);
            break;
        }
      });
    };
  }

  // TODO: unused now, but should be used in highlighter
  mouseOutHighlight(selector) {
    const self = this;

    return function(d) {
      self.svg.selectAll(`.serie-${selector}`).each(() => {
        const tag = d3.select(this);
        const tagName = tag.node().tagName;

        switch (tagName) {
          case 'path':
          case 'line':
            tag.transition()
              .duration(200)
              .style('stroke-width', '2px');
            break;
          case 'circle':
            tag.transition()
              .duration(200)
              .style('r', 3);
            break;
        }
      });
    };
  }

  createBaseGraph(data) {
    // TODO: remove in D3 4.0 - https://github.com/d3/d3-selection/issues/43
    const self = this;

    const svg = d3.select(this.element).select('svg')
      .attr('width', self.opt('width'))
      .attr('height', self.opt('height'))
      .style('width', self.opt('width') + 'px')
      .style('height', self.opt('height') + 'px')
      .classed('zclite-d3', true)
      .classed('svg-content', true);

    const xExtent = d3.extent(data, d => d.date);

    // TODO: hack... make extent bit larger, so that bar charts have padding
    if(self.opt('isBarChart')) {
      xExtent[0] = moment(xExtent[0]).subtract(8, 'hours').toDate();
      xExtent[1] = moment(xExtent[1]).add(8, 'hours').toDate();
    }

    const xScale = d3.time.scale()
      .domain(xExtent)
      .range([self.opt('padding.left'), self.opt('width') - self.opt('padding.right')]);

    // X axis
    const xAxis = d3.svg.axis()
      .scale(xScale)
      .orient('bottom')
      .innerTickSize(0)
      .outerTickSize(5)
      .ticks(6)
      .tickFormat(self.opt('timeFormats.day'));

    svg.append('g')
      .attr('class', 'axis xaxis')
      .attr('transform', 'translate(0,' + (self.opt('height') - self.opt('padding.bottom')) + ')')
      .call(xAxis);

    // Rotate xAxis texts
    svg.selectAll('.xaxis text')
      .attr('transform', function(d) {
        return 'translate(' + this.getBBox().height * -2 + ',' + this.getBBox().height + ') rotate(-45)';
      });

    this.svg = svg;
    this.xScale = xScale;

    return {
      svg,
      xScale
    };
  }

  createTrendLine(params) {
    const self = this;
    const yScale = this.scales[params.yAxis];
    const xScale = this.xScale;

    const trendData = _.map(this.createTrendData(self.data, params.y), (d, i) => {
      return {
        date: self.data[i].date,
        value: d
      };
    });
    const trendLine = d3.svg.line()
      .interpolate('monotone')
      .x(d => xScale(d.date))
      .y(d => yScale(d.value));

    self.svg.append('path')
      .datum(trendData)
      .classed('line', true)
      .classed(`serie-${params.name}-trend`, true)
      .attr('d', trendLine)
      .attr('fill', 'none')
      .attr('stroke', params.color)
      .attr('stroke-width', 2)
      .attr('opacity', 0.75);
  }

  createTrendData(orderedData, propertyName) {
    const lastIndex = orderedData.length;
    const trends = _.map(orderedData, hourlyData => {
      const index = orderedData.indexOf(hourlyData);

      // take 2 before and after
      const minIndex = index - 2 < 0 ? 0 : index - 2;
      const maxIndex = index + 3 >= lastIndex ? lastIndex : index + 3;
      const trendRanges = _.range(minIndex, maxIndex);

      const trendValues = _.chain(trendRanges)
        .map(idx => _.get(orderedData[idx], propertyName, 0))
        .sum()
        .value();

      return trendValues / trendRanges.length;
    });

    return trends;
  }

  createVerticalPositionIndicator() {
    const self = this;
    const xScale = this.xScale;

    const vertical = d3.select($(this.element)[0])
      .append('div')
      .attr('class', 'remove position-indicator')
      .style('position', 'absolute')
      //.style("z-index", "19")
      .style('width', '1px')
      .style('height', self.svg.attr('height'))
      .style('top', self.opt('padding.top') + 'px')
      .style('bottom', self.opt('padding.bottom') + 'px')
      .style('left', '0px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('background', 'rgba(0,0,0,0.1)');

    self.svg.append('rect')
      .attr('width', this.opt('width'))
      .attr('height', this.opt('height'))
      .style('z-index', 99999)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mousemove', function() {
        const mousex = d3.mouse(this)[0];
        const invertx = self.xScale.invert(mousex);
        const date = self.bisectDate(self.data, invertx, 1);
        const d0 = self.data[date - 1];
        const d1 = self.data[date];
        let d = d0;
        if(d1 && d0) {
          d = invertx - d0.date > d1.date - invertx ? d1 : d0;
        }

        vertical.style('left', xScale(d.date) + 'px' );

        self.tooltipDiv.html(self.tooltipFn(d, self._options))
          .style('left', (d3.event.pageX + 10) + 'px')
          .style('top', (d3.event.pageY - 28) + 'px');
        self.tooltipDiv
          .style('opacity', 0.9);
        vertical.style('opacity', 1);
      })
      .on('mouseover', function() {
        let mousex = d3.mouse(this);
        mousex = mousex[0];
        vertical.style('left', mousex + 'px');
        vertical.style('opacity', 1);

        self.tooltipDiv.transition()
          .duration(200)
          .style('opacity', 0.9);
      })
      .on('mouseout', function() {
        vertical.style('opacity', 0);

        self.tooltipDiv.transition()
          .duration(300)
          .style('opacity', 0);
      });
  }
}



// WEBPACK FOOTER //
// ./src/libs/zcd3.js